<script setup>
import { computed, ref, watch, nextTick } from "vue";
import { mdiClose } from "@mdi/js";
import Buttons from "@/Components/Buttons.vue";
import CardBox from "@/Components/CardBox.vue";
import OverlayLayer from "@/Components/OverlayLayer.vue";
import CardBoxComponentTitle from "@/Components/CardBoxComponentTitle.vue";
import { isIntegerKey } from "@vue/shared";

const props = defineProps({
	title: {
		type: String,
		required: true,
	},
	button: {
		type: String,
		default: "info",
	},
	buttonLabel: {
		type: String,
		default: "Aceptar",
	},
	hasCancel: Boolean,
	noButtons: Boolean,
	modelValue: {
		type: [String, Number, Boolean],
		default: null,
	},
	sizeModal: {
		type: String,
		default: "",
	},
	isFullScreen: Boolean,
	zIndex: {
		type: String,
		default: "z-50",
	},
});

// Tamaños de modal reactivos
const clasModal = computed(() => {
	const z = props.zIndex || 'z-50';
	if (props.sizeModal == 'small') {
		return `shadow-lg ${z}`
	} else if (props.sizeModal == 'full') {
		return `shadow-lg container max-h-[95vh] flex flex-col ${z}`
	} else if (props.sizeModal == 'formfull') {
		return `shadow-lg ${z}`
	} else if (props.sizeModal == 'ajuste') {
		return `shadow-lg container mx-auto ${z}`
	} else if (props.sizeModal == 'medium') {
		return `shadow-lg max-h-[90vh] flex flex-col ${z} w-full sm:w-11/12 lg:w-4/5 xl:w-9/12`
	} else if (props.sizeModal == 'large') {
		return `shadow-lg ${z} w-11/12 md:w-11/12 lg:w-4/5 xl:w-3/4 max-w-5xl max-h-[90vh] flex flex-col`
	} else if (props.sizeModal == 'preview') {
		return `shadow-lg ${z} max-h-[80vh] max-w-[80vw]`
	} else if (props.sizeModal == 'presentation') {
		return `shadow-lg ${z} h-[100vh] w-[100vw] max-h-[100vh] max-w-[100vw]`
	}

	return `shadow-lg max-h-[90vh] flex flex-col w-11/12 md:w-3/5 lg:w-2/5 xl:w-4/12 ${z}`
})


const emit = defineEmits(["update:modelValue", "cancel", "confirm"]);

const value = computed({
	get: () => props.modelValue,
	set: (value) => emit("update:modelValue", value),
});

// IMG mode: detectar imagen dentro del slot y ajustar modal a sus dimensiones
const imgStyle = ref({})
const contentRef = ref(null)
const isCurrentImage = ref(false)

const isImageExt = (src) => {
	const ext = src.split('.').pop().split('?')[0].split('#')[0].toLowerCase()
	return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(ext)
}

watch(value, async (newVal) => {
	if (newVal && (props.sizeModal === 'preview' || props.sizeModal === 'presentation')) {
		await nextTick()
		const el = contentRef.value?.querySelector('embed, img')
		if (el) {
			const src = el.src || el.getAttribute('src')
			if (src && isImageExt(src)) {
				isCurrentImage.value = true
				const img = new Image()
				img.onload = () => {
					// El modal tiene un max-h y max-w definido en clasModal
					// Además tiene un header y padding p-4 (32px horizontal, 54px vertical aprox)
					const isPresentation = props.sizeModal === 'presentation'
					const headerH = 42
					const paddingW = 32
					const paddingH = 32

					const viewRatio = isPresentation ? 0.96 : 0.8
					
					const maxW = window.innerWidth * viewRatio - paddingW
					const maxH = window.innerHeight * viewRatio - headerH - paddingH - 20
					
					let w = img.naturalWidth
					let h = img.naturalHeight
					
					// Escalar proporcionalmente si excede el viewport del modal
					const ratio = Math.min(maxW / w, maxH / h, 1)
					if (isPresentation) {
						// En modo presentación, forzar a llegar a uno de los límites si es posible
						const ratioFill = Math.min(maxW / w, maxH / h)
						w = Math.round(w * ratioFill)
						h = Math.round(h * ratioFill)
					} else {
						w = Math.round(w * ratio)
						h = Math.round(h * ratio)
					}

					// Ajustamos el estilo del modal
					if (isPresentation) {
						imgStyle.value = { 
							width: '100vw', 
							height: '100vh'
						}
					} else {
						imgStyle.value = { 
							width: (w + paddingW) + 'px', 
							height: (h + headerH + paddingH) + 'px',
							maxHeight: '80vh'
						}
					}
				}
				img.src = src
			} else {
				// No es imagen (pdf, docx, etc.) -> usar tamaño full inteligente
				isCurrentImage.value = false
				if (props.sizeModal === 'presentation') {
					imgStyle.value = { width: '100vw', height: '100vh' }
				} else {
					imgStyle.value = { width: '80vw', height: '80vh' }
				}
			}
		}
	} else {
		imgStyle.value = {}
		isCurrentImage.value = false
	}
})

watch(value, (newVal) => {
    // Añadimos la condición && props.sizeModal === 'presentation'
    if (newVal && props.isFullScreen && props.sizeModal === 'presentation') {
        if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen();
        }
    } else if (!newVal && document.fullscreenElement) {
        document.exitFullscreen();
    }
});


const confirmCancel = (mode) => {
	value.value = false;// LRR creo que cierra el modal CardBoxmodal
	emit(mode);

	// LRR
	//if (mode == "confirm") {
	//resultConfirm.value = true
	//}
	//console.log(emit(mode))

};

const confirm = () => confirmCancel("confirm");

const cancel = () => confirmCancel("cancel");

window.addEventListener("keydown", (e) => {
	if (e.key === "Escape" && value.value) {
		cancel();
	}
});
// aqui
</script>

<template>
	<OverlayLayer v-show="value" :z-index="zIndex" @overlay-click="cancel">
		<CardBox v-show="value" :class="[clasModal, (sizeModal === 'preview' || sizeModal === 'presentation') ? 'overflow-auto' : '']" :style="(sizeModal === 'preview' || sizeModal === 'presentation') ? imgStyle : {}" is-modal>
			<CardBoxComponentTitle :title="title">
				<Buttons v-if="hasCancel" color="light" size="roundedSm" @click.prevent="cancel">
					<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
						<path :d="mdiClose" />
					</svg>
				</Buttons>
			</CardBoxComponentTitle>

			<div ref="contentRef" :class="(sizeModal == 'preview' || sizeModal == 'presentation') ? 'relative flex-1 h-[calc(100%-40px)] w-full flex items-center justify-center' : 'space-y-3 overflow-y-auto max-h-[calc(90vh-130px)] pr-1'">
				<slot />
			</div>

			<template v-if="!noButtons && sizeModal != 'full' && sizeModal != 'formfull' && sizeModal != 'large' && sizeModal != 'preview'" #footer>
				<div class="flex items-center gap-2 justify-end">
					<Buttons :color="button === 'danger' ? 'red' : 'blue'" @click="confirm">
						{{ buttonLabel }}
					</Buttons>
					<Buttons v-if="hasCancel" color="light" @click="cancel">
						Cancelar
					</Buttons>
				</div>
			</template>
		</CardBox>
	</OverlayLayer>
</template>
